import { DEFAULT_STUDENT_THEME, STUDENT_THEME_COLOR_TOKENS, type StudentThemeConfig } from '@pustak-lms/contracts';

/** Resolve the complete stored theme into the CSS variables used by the Student app. */
export function studentThemeToCssVariables(theme: StudentThemeConfig = DEFAULT_STUDENT_THEME): Record<string, string> {
  const variables: Record<string, string> = {};
  for (const [key, definition] of Object.entries(STUDENT_THEME_COLOR_TOKENS)) {
    variables[definition.cssVariable] = theme.colors[key as keyof typeof theme.colors];
  }
  const c = theme.colors;
  Object.assign(variables, {
    '--error': c.error, '--error-foreground': c.errorForeground,
    '--error-soft': c.errorSoft, '--error-soft-foreground': c.errorSoftForeground,
    '--lms-radius-control': `${theme.shape.controlRadius}px`,
    '--lms-radius-card': `${theme.shape.cardRadius}px`,
    '--lms-radius-dialog': `${theme.shape.dialogRadius}px`,
    '--lms-font-body': theme.typography.bodyFont === 'inter' ? 'Inter, ui-sans-serif, system-ui, sans-serif' : 'ui-sans-serif, system-ui, sans-serif',
    '--lms-font-heading': theme.typography.headingFont === 'jakarta' ? '"Plus Jakarta Sans", Inter, ui-sans-serif, system-ui, sans-serif' : theme.typography.headingFont === 'inter' ? 'Inter, ui-sans-serif, system-ui, sans-serif' : 'ui-sans-serif, system-ui, sans-serif',
    '--lms-shadow-card': '0 1px 3px color-mix(in srgb, var(--theme-shadow) 6%, transparent)',
    '--lms-shadow-popover': '0 12px 32px color-mix(in srgb, var(--theme-shadow) 12%, transparent)',
  });
  return variables;
}

export const DEFAULT_PRIMARY_COLOR = "#123F43";
export const DEFAULT_SECONDARY_COLOR = "#C89743";

export interface StudentBrandTokens {
  "--primary": string;
  "--primary-foreground": string;
  "--primary-hover": string;
  "--primary-active": string;
  "--secondary": string;
  "--secondary-foreground": string;
  "--secondary-hover": string;
  "--accent": string;
  "--accent-foreground": string;
  "--gold": string;
  "--gold-foreground": string;
  "--gold-hover": string;
  "--ring": string;
}

const HEX = /^#([0-9a-f]{6})$/i;
const channels = (hex: string) => [1, 3, 5].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16)) as [number, number, number];
const color = (values: readonly number[]) => `#${values.map((value) => Math.round(Math.min(255, Math.max(0, value))).toString(16).padStart(2, "0")).join("").toUpperCase()}`;
const blend = (hex: string, target: "#000000" | "#FFFFFF", amount: number) => {
  const from = channels(hex);
  const to = channels(target);
  return color(from.map((value, index) => value + (to[index]! - value) * amount));
};

export function normalizeBrandColor(value: string | null | undefined, fallback: string): string {
  return value?.match(HEX) ? value.toUpperCase() : fallback;
}

export function relativeLuminance(hex: string): number {
  const linear = channels(normalizeBrandColor(hex, "#000000")).map((value) => {
    const channel = value / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * linear[0]! + 0.7152 * linear[1]! + 0.0722 * linear[2]!;
}

export function contrastRatio(first: string, second: string): number {
  const values = [relativeLuminance(first), relativeLuminance(second)].sort((a, b) => b - a);
  return (values[0]! + 0.05) / (values[1]! + 0.05);
}

function foreground(background: string): "#000000" | "#FFFFFF" {
  return contrastRatio(background, "#000000") >= contrastRatio(background, "#FFFFFF") ? "#000000" : "#FFFFFF";
}

function interactive(base: string, text: "#000000" | "#FFFFFF", amount: number) {
  const candidate = blend(base, text === "#FFFFFF" ? "#000000" : "#FFFFFF", amount);
  return contrastRatio(candidate, text) >= 4.5 ? candidate : base;
}

function soft(base: string) {
  const background = blend(base, "#FFFFFF", 0.88);
  return { background, foreground: foreground(background) };
}

export function deriveStudentBrandTokens(primaryInput?: string | null, secondaryInput?: string | null): StudentBrandTokens {
  const primary = normalizeBrandColor(primaryInput, DEFAULT_PRIMARY_COLOR);
  const secondary = normalizeBrandColor(secondaryInput, DEFAULT_SECONDARY_COLOR);
  const primaryForeground = foreground(primary);
  const secondaryForeground = foreground(secondary);
  const primarySoft = soft(primary);
  const secondarySoft = soft(secondary);
  return {
    "--primary": primary,
    "--primary-foreground": primaryForeground,
    "--primary-hover": interactive(primary, primaryForeground, 0.12),
    "--primary-active": interactive(primary, primaryForeground, 0.22),
    "--secondary": primarySoft.background,
    "--secondary-foreground": primarySoft.foreground,
    "--secondary-hover": interactive(primarySoft.background, primarySoft.foreground, 0.06),
    "--accent": secondarySoft.background,
    "--accent-foreground": secondarySoft.foreground,
    "--gold": secondary,
    "--gold-foreground": secondaryForeground,
    "--gold-hover": interactive(secondary, secondaryForeground, 0.12),
    "--ring": primary
  };
}
